/**
 * frontend/src/utils/periodHelper.js
 *
 * Shared period / year helper for:
 * - year extraction from payloads
 * - labels
 * - comparison-year derivation
 * - unified PageShell / CompanyContext support
 */

export function toYear(v) {
  if (v === null || v === undefined || v === "") return null;

  if (typeof v === "number") {
    return Number.isFinite(v) && v > 1900 && v < 2100 ? Math.trunc(v) : null;
  }

  const text = String(v).trim();
  if (!text) return null;

  const parsed = parseInt(text, 10);
  if (Number.isFinite(parsed) && parsed > 1900 && parsed < 2100) return parsed;

  const match = text.match(/\b(19|20)\d{2}\b/);
  return match ? Number(match[0]) : null;
}

export function pickText(...values) {
  for (const value of values) {
    if (value === null || value === undefined) continue;
    const text = String(value).trim();
    if (text) return text;
  }
  return "";
}

export function toNumber(value, fallback = null) {
  if (value === null || value === undefined || value === "") return fallback;

  if (typeof value === "number") {
    return Number.isFinite(value) ? value : fallback;
  }

  if (typeof value === "boolean") {
    return value ? 1 : 0;
  }

  const cleaned = String(value)
    .replace(/,/g, "")
    .replace(/\$/g, "")
    .replace(/%/g, "")
    .replace(/\s+/g, "")
    .trim();

  if (!cleaned) return fallback;

  const parsed = Number(cleaned);
  return Number.isFinite(parsed) ? parsed : fallback;
}

export function normalizePeriodValue(value) {
  if (value === null || value === undefined || value === "") return null;

  const year = toYear(value);
  if (year) return year;

  const text = String(value).trim();
  if (!text) return null;

  const fyMatch = text.match(/FY\s*([0-9]{4})/i);
  if (fyMatch) return Number(fyMatch[1]);

  const yearMatch = text.match(/\b(19|20)\d{2}\b/);
  if (yearMatch) return Number(yearMatch[0]);

  return text.replace(/\s+/g, " ").trim();
}

export function expandYearRange(startYear, endYear) {
  const start = toYear(startYear);
  const end = toYear(endYear);

  if (!start && !end) return [];
  if (start && !end) return [start];
  if (!start && end) return [end];
  if (end < start) return [start];

  const out = [];
  for (let y = start; y <= end; y += 1) out.push(y);
  return out;
}

export function uniqueSortedYears(values = []) {
  const raw = new Set();

  values.forEach((v) => {
    const y = toYear(v);
    if (y) raw.add(y);
  });

  return [...raw].sort((a, b) => a - b);
}

export function extractYears(payload) {
  if (!payload) return [];
  const raw = new Set();

  for (const key of [
    "years_detected",
    "years_available",
    "fiscal_years",
    "history_years",
    "trend_years",
    "available_years",
  ]) {
    const arr = payload?.[key];
    if (Array.isArray(arr)) {
      arr.forEach((y) => {
        const v = toYear(y);
        if (v) raw.add(v);
      });
    }
  }

  for (const key of ["year", "fiscal_year", "current_year", "latest_year"]) {
    const v = toYear(payload?.[key]);
    if (v) raw.add(v);
  }

  const sub = payload?.latest_submission || payload?.submission || {};
  const subYear = toYear(sub?.fiscal_year ?? sub?.year ?? sub?.current_year);
  if (subYear) raw.add(subYear);

  const accountingPeriod =
    payload?.summary?.accounting_period ||
    payload?.accounting_period ||
    {};

  const collectRange = (obj) => {
    expandYearRange(obj?.period_start_year, obj?.period_end_year).forEach((y) => raw.add(y));
  };

  collectRange(payload);
  collectRange(sub);
  collectRange(accountingPeriod);

  const historyLike = [
    ...(Array.isArray(payload?.history) ? payload.history : []),
    ...(Array.isArray(payload?.trend) ? payload.trend : []),
    ...(Array.isArray(payload?.score_history) ? payload.score_history : []),
    ...(Array.isArray(payload?.timeseries) ? payload.timeseries : []),
    ...(Array.isArray(payload?.series) ? payload.series : []),
    ...(Array.isArray(payload?.periods) ? payload.periods : []),
  ];

  historyLike.forEach((item) => {
    const y = toYear(
      item?.year ??
      item?.fiscal_year ??
      item?.current_year ??
      item?.period ??
      item?.label
    );
    if (y) raw.add(y);
  });

  return [...raw].sort((a, b) => a - b);
}

export function extractYearsFromPayload(payload) {
  return extractYears(payload);
}

export function latestYear(payload) {
  const years = extractYears(payload);
  return years.length > 0 ? years[years.length - 1] : null;
}

export function isMultiYear(payload) {
  return (
    extractYears(payload).length > 1 ||
    Boolean(payload?.multi_year) ||
    Boolean(payload?.has_multi_year)
  );
}

export function yearLabel(year) {
  const y = toYear(year);
  return y ? `FY${y}` : "�";
}

export function formatYearsLabel(input) {
  const years = Array.isArray(input)
    ? uniqueSortedYears(input)
    : extractYears(input);

  if (years.length === 0) return "No fiscal years detected";
  if (years.length === 1) return `FY${years[0]}`;
  return `FY${years[0]}�${years[years.length - 1]}`;
}

export function yearsRangeLabel(payload) {
  return formatYearsLabel(payload);
}

export function buildPeriodLabel(period, year) {
  const y = toYear(year);
  const cleanPeriod =
    period !== null && period !== undefined && String(period).trim() !== ""
      ? String(period).trim()
      : "";

  if (cleanPeriod && y) {
    if (cleanPeriod === String(y) || cleanPeriod.toUpperCase() === `FY${y}`) {
      return `FY${y}`;
    }
    return `${cleanPeriod} � FY${y}`;
  }

  if (y) return `FY${y}`;
  if (cleanPeriod) return cleanPeriod;
  return "Current";
}

export function comparisonLabel(currentYear, priorYear) {
  const c = toYear(currentYear);
  const p = toYear(priorYear);

  if (!c) return "";
  if (!p) return yearLabel(c);

  return `FY${c} vs FY${p}`;
}

export function yearOptions(payload) {
  return extractYears(payload)
    .slice()
    .sort((a, b) => b - a)
    .map((y) => ({
      value: y,
      label: `FY${y}`,
    }));
}

export function buildPeriodContext(companyOrConfig, selectedYearArg = null) {
  let company = companyOrConfig;
  let selectedYear = selectedYearArg;
  let comparisonYear = null;
  let period = null;
  let mode = "single";

  if (
    companyOrConfig &&
    typeof companyOrConfig === "object" &&
    !Array.isArray(companyOrConfig) &&
    (
      Object.prototype.hasOwnProperty.call(companyOrConfig, "company") ||
      Object.prototype.hasOwnProperty.call(companyOrConfig, "selectedYear") ||
      Object.prototype.hasOwnProperty.call(companyOrConfig, "comparisonYear") ||
      Object.prototype.hasOwnProperty.call(companyOrConfig, "availableYears")
    )
  ) {
    const cfg = companyOrConfig;
    company = cfg.company ?? cfg.data ?? null;
    selectedYear = cfg.selectedYear ?? null;
    comparisonYear = cfg.comparisonYear ?? null;
    period = cfg.period ?? null;
    mode = cfg.mode ?? (comparisonYear ? "compare" : "single");

    const configYears = Array.isArray(cfg.availableYears)
      ? uniqueSortedYears(cfg.availableYears)
      : [];

    const companyYears = extractYears(company);
    const years = uniqueSortedYears([...configYears, ...companyYears]).sort((a, b) => a - b);

    const latest = years.length > 0 ? years[years.length - 1] : null;
    const active = toYear(selectedYear) || latest;
    const prior =
      toYear(comparisonYear) ||
      (active && years.length > 1
        ? [...years].sort((a, b) => b - a).find((y) => y < active) || null
        : null);

    return {
      years: [...years].sort((a, b) => b - a),
      availableYears: [...years].sort((a, b) => b - a),
      latest,
      latestYear: latest,
      active,
      activeYear: active,
      selectedYear: active,
      prior,
      priorYear: prior,
      comparisonYear: prior,
      multiYear: years.length > 1,
      isComparisonMode: mode === "compare" && !!active && !!prior,
      mode,
      activeLabel: yearLabel(active),
      periodLabel: buildPeriodLabel(period, active),
      rangeLabel: formatYearsLabel(years),
      availableYearsLabel: formatYearsLabel(years),
      comparisonLabel: prior ? comparisonLabel(active, prior) : yearLabel(active),
      yearOptions: [...years]
        .sort((a, b) => b - a)
        .map((y) => ({ value: y, label: `FY${y}` })),
    };
  }

  const years = extractYears(company);
  const latest = years.length > 0 ? years[years.length - 1] : null;
  const active = toYear(selectedYear) || latest;
  const prior =
    active && years.length > 1
      ? [...years].sort((a, b) => b - a).find((y) => y < active) || null
      : null;

  return {
    years: [...years].sort((a, b) => b - a),
    availableYears: [...years].sort((a, b) => b - a),
    latest,
    latestYear: latest,
    active,
    activeYear: active,
    selectedYear: active,
    prior,
    priorYear: prior,
    comparisonYear: prior,
    multiYear: years.length > 1,
    isComparisonMode: false,
    mode: "single",
    activeLabel: yearLabel(active),
    periodLabel: buildPeriodLabel(null, active),
    rangeLabel: formatYearsLabel(years),
    availableYearsLabel: formatYearsLabel(years),
    comparisonLabel: prior ? comparisonLabel(active, prior) : yearLabel(active),
    yearOptions: [...years]
      .sort((a, b) => b - a)
      .map((y) => ({ value: y, label: `FY${y}` })),
  };
}
